import React, { useState } from 'react';
import { Category } from 'types';
import { getAllFieldsValuesWithLabels, Tree } from 'utils/parserTree';

type CampaignLabelProps = {
  value: string;
  label: string;
  rules: string[];
  selected: boolean;
  negated: boolean;
  filterRules: string[];
  notFilterRules: string[];
  rulesData: {
    value: string;
    label: string;
  }[];
  rulesNegatedData: {
    value: string;
    label: string;
  }[];
  handleDelete: (labelId: string, exclude: boolean) => void;
};

const CampaignLabel: React.FC<CampaignLabelProps> = ({
  value,
  label,
  rules,
  selected,
  negated,
  filterRules,
  notFilterRules,
  rulesData,
  rulesNegatedData,
  handleDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDeleteCampaign = (): void => {
    handleDelete(value, negated);
    rules.forEach((rule) => {
      handleDelete(rule, negated);
    });
  };

  return (
    <>
      <div
        role="button"
        aria-hidden
        className="relative flex justify-between items-center py-1 text-small"
      >
        <div
          aria-hidden
          className="flex items-center mr-1"
          onClick={(): void => handleDeleteCampaign()}
        >
          <input
            id={`${value}-show-label`}
            data-testid={`${value}-show-label`}
            type="checkbox"
            className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer ${
              negated ? 'litlingo-checkbox-negated' : ''
            }`}
            checked={selected || negated}
          />
        </div>
        <button
          type="button"
          onClick={(e): void => {
            e.stopPropagation();
            if (rules.length > 0) {
              setIsOpen(!isOpen);
            }
          }}
          className="flex flex-1 text-white cursor-pointer focus:outline-none"
        >
          <span className="select-none break-all">{label} </span>
        </button>
        {rules.length > 0 && (
          <button
            type="button"
            onClick={(e): void => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="h-4 pl-1 focus:outline-none"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`${isOpen ? 'transform rotate-90' : ''}`}
            >
              <path
                d="M2.08961 9.91604C2.1425 9.96937 2.21437 9.99955 2.28948 9.99999C2.32675 10.0003 2.3637 9.99307 2.39807 9.97864C2.43244 9.9642 2.4635 9.9429 2.48934 9.91604L7.20219 5.20319C7.22866 5.17707 7.24968 5.14595 7.26402 5.11164C7.27837 5.07733 7.28575 5.04051 7.28575 5.00333C7.28575 4.96614 7.27837 4.92932 7.26402 4.89501C7.24968 4.8607 7.22866 4.82958 7.20219 4.80346L2.48934 0.0896109C2.46343 0.0619091 2.43221 0.0397035 2.39754 0.0243155C2.36287 0.00892748 2.32545 0.000671647 2.28753 3.93014e-05C2.2496 -0.000593044 2.21193 0.00641102 2.17677 0.0206347C2.1416 0.0348583 2.10966 0.0560109 2.08283 0.0828334C2.05601 0.109656 2.03486 0.1416 2.02063 0.176766C2.00641 0.211931 1.99941 0.249598 2.00004 0.287526C2.00067 0.325453 2.00893 0.362866 2.02432 0.397537C2.0397 0.432209 2.06191 0.46343 2.08961 0.489344L6.6026 5.00333L2.08961 9.51631C2.06326 9.5425 2.04235 9.57364 2.02809 9.60794C2.01382 9.64224 2.00647 9.67903 2.00647 9.71618C2.00647 9.75333 2.01382 9.79011 2.02809 9.82441C2.04235 9.85871 2.06326 9.88985 2.08961 9.91604Z"
                fill="#ffffff"
              />
            </svg>
          </button>
        )}
      </div>
      {isOpen &&
        rules.map((rule) => {
          const node = [...rulesData, ...rulesNegatedData]?.find((item) => item.value === rule);
          if (!node) return null;
          const { value: ruleValue, label: ruleLabel } = node;
          const ruleSelected = filterRules.includes(rule);
          const ruleNegated = notFilterRules.includes(rule);

          return (
            <div
              key={ruleValue}
              role="button"
              aria-hidden
              className="py-1 pl-1 relative flex justify-start items-center bg-litlingo-success-light bg-opacity-10 text-small"
              onClick={(): void => handleDelete(ruleValue, ruleNegated)}
            >
              <div className="flex items-center mr-1">
                <input
                  id={`${ruleValue}-show-label`}
                  data-testid={`${ruleValue}-show-label`}
                  type="checkbox"
                  className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer ${
                    ruleNegated ? 'litlingo-checkbox-negated' : ''
                  }`}
                  checked={ruleSelected || ruleNegated}
                />
              </div>
              <label htmlFor={ruleValue} className="text-white cursor-pointer">
                <span className="select-none break-all">{ruleLabel} </span>
              </label>
            </div>
          );
        })}
    </>
  );
};

type ComponentProps = {
  handleDelete: (labelId: string, exclude: boolean) => void;
  filterCampaigns: string[];
  notFilterCampaigns: string[];
  filterRules: string[];
  notFilterRules: string[];
  campaignsKey: string;
  rulesKey: string;
  categories: Category[];
  tree: Tree;
};
const ShowLabelsCategories: React.FC<ComponentProps> = ({
  handleDelete,
  filterCampaigns,
  notFilterCampaigns,
  filterRules,
  notFilterRules,
  campaignsKey,
  rulesKey,
  categories,
  tree,
}) => {
  const campaignsData = getAllFieldsValuesWithLabels(tree)[campaignsKey] || [];
  const campaignsNegatedData = getAllFieldsValuesWithLabels(tree)[`not_${campaignsKey}`] || [];
  const rulesData = getAllFieldsValuesWithLabels(tree)[rulesKey] || [];
  const rulesNegatedData = getAllFieldsValuesWithLabels(tree)[`not_${rulesKey}`] || [];

  return (
    <div className="flex flex-col">
      {[...filterCampaigns, ...notFilterCampaigns].map((labelId) => {
        const selected = filterCampaigns.includes(labelId);
        const negated = notFilterCampaigns.includes(labelId);
        const node = [...campaignsData, ...campaignsNegatedData]?.find(
          (item) => item.value === labelId
        );
        const rules = [...filterRules, ...notFilterRules].filter((r) => {
          const ruleCategory = categories.find(
            (category) =>
              (category.rule?.rule_group_uuid === r || category.rule_revision_uuid === r) &&
              category.model_uuid === node?.value &&
              !category.model?.deleted_by_uuid
          );

          return !!ruleCategory;
        });

        if (node) {
          const { label, value } = node;
          return (
            <CampaignLabel
              value={value}
              label={label}
              rules={rules}
              selected={selected}
              negated={negated}
              filterRules={filterRules}
              notFilterRules={notFilterRules}
              rulesData={rulesData}
              rulesNegatedData={rulesNegatedData}
              handleDelete={handleDelete}
            />
          );
        }
        return null;
      })}
      {[...filterRules, ...notFilterRules]
        .filter((rule) => {
          if (
            !categories.find(
              (category) =>
                category.rule?.rule_group_uuid === rule || category.rule_revision_uuid === rule
            )
          ) {
            return true;
          }

          if (
            categories.find(
              (category) =>
                (category.rule?.rule_group_uuid === rule || category.rule_revision_uuid === rule) &&
                [...campaignsData, ...campaignsNegatedData].every(
                  (c) => category.model_uuid !== c.value
                ) &&
                !category.model?.deleted_by_uuid
            )
          ) {
            return true;
          }
          return false;
        })
        .map((labelId) => {
          const selected = filterRules.includes(labelId);
          const negated = notFilterRules.includes(labelId);
          const node = [...rulesData, ...rulesNegatedData]?.find((item) => item.value === labelId);

          if (node) {
            const { label, value } = node;
            return (
              <div
                role="button"
                aria-hidden
                className="relative flex justify-between items-center py-1 text-small"
                onClick={(): void => handleDelete(value, negated)}
              >
                <div className="flex justify-start items-center">
                  <div className="flex items-center mr-1">
                    <input
                      id={`${value}-show-label`}
                      data-testid={`${value}-show-label`}
                      type="checkbox"
                      className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer ${
                        negated ? 'litlingo-checkbox-negated' : ''
                      }`}
                      checked={selected || negated}
                    />
                  </div>
                  <label htmlFor={value} className="text-white cursor-pointer">
                    <span className="select-none break-all">{label} </span>
                  </label>
                </div>
              </div>
            );
          }
          return null;
        })}
    </div>
  );
};

export default ShowLabelsCategories;
